import React, { useCallback, useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw, AlertTriangle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getQueuedActions, getFailedActions, removeFailedAction, retryFailedAction } from "../utils/indexedDb";

const ACTION_LABELS = {
  CREATE_ORDER: "Open table",
  CREATE_PARCEL: "New parcel",
  UPDATE_ITEMS: "Update items",
  CANCEL_ORDER: "Cancel order",
  CLOSE_ORDER: "Close order",
  BILL_FROM_ORDER: "Bill",
  KOT_PRINT: "KOT",
  TRANSFER_TABLE: "Table transfer",
  MERGE_ORDERS: "Merge orders",
};

const describe = (a) => {
  const parts = [ACTION_LABELS[a.type] || a.type];
  if (a.data?.sectionName) parts.push(`${a.data.sectionName} ${a.data.unitNumber || ""}`.trim());
  if (a.type === "BILL_FROM_ORDER" && a.data?.name) parts.push(a.data.name);
  return parts.join(" · ");
};

const SyncIndicator = ({ isSyncing }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failed, setFailed] = useState([]);
  const [showFailed, setShowFailed] = useState(false);
  const location = useLocation();

  const refreshQueue = useCallback(async () => {
    const [queue, failedActions] = await Promise.all([getQueuedActions(), getFailedActions()]);
    setPendingCount(queue.length);
    setFailed(failedActions);
  }, []);

  useEffect(() => {
    refreshQueue();
    window.addEventListener("sync-queue-changed", refreshQueue);
    return () => window.removeEventListener("sync-queue-changed", refreshQueue);
  }, [refreshQueue]);

  const retryAll = async () => {
    // Re-queue in the original order so dependent changes replay after their order
    for (const f of failed) await retryFailedAction(f);
    setShowFailed(false);
    window.dispatchEvent(new Event("cloudrasoi-sync-request"));
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (location.pathname.startsWith("/r/") || location.pathname.startsWith("/table/")) {
    return null;
  }

  if (failed.length > 0) {
    return (
      <div className="fixed bottom-4 right-4 z-50 print:hidden select-none max-w-[calc(100vw-2rem)]">
        {showFailed && (
          <div className="mb-2 w-80 max-w-full bg-white rounded-2xl shadow-2xl border border-red-200 p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-slate-800">Changes the server rejected</span>
              <button onClick={() => setShowFailed(false)} className="text-slate-400 hover:text-slate-600" title="Hide">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-2 max-h-60 overflow-y-auto">
              {failed.map((f) => (
                <li key={f.id} className="border border-slate-100 rounded-lg p-2">
                  <div className="font-semibold text-slate-700">{describe(f)}</div>
                  <div className="text-xs text-red-600">{f.error}</div>
                  <div className="text-[10px] text-slate-400">
                    Saved {new Date(f.queuedAt || f.failedAt).toLocaleString("en-IN")}
                  </div>
                  <button
                    onClick={() => removeFailedAction(f.id)}
                    className="mt-1 text-xs text-slate-500 hover:text-red-600 underline"
                  >
                    Discard
                  </button>
                </li>
              ))}
            </ul>
            <button
              onClick={retryAll}
              className="mt-3 w-full bg-teal-600 hover:bg-teal-700 text-white rounded-lg py-1.5 font-semibold"
            >
              Retry all
            </button>
          </div>
        )}
        <button
          onClick={() => setShowFailed((v) => !v)}
          className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg border border-red-500 text-sm font-semibold"
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap">{failed.length} change(s) failed to sync</span>
        </button>
      </div>
    );
  }

  if (isSyncing) {
    return (
      <div className="hidden md:block fixed bottom-4 right-4 z-50 print:hidden select-none">
        <div className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-full shadow-lg border border-teal-500 text-sm font-semibold">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Syncing orders...</span>
        </div>
      </div>
    );
  }

  if (isOnline) {
    return (
      <div className="hidden md:block fixed bottom-4 right-4 z-50 print:hidden select-none">
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            title="Show status"
            className="text-teal-400 bg-slate-900/90 p-1.5 rounded-full shadow-md border border-slate-800 hover:bg-slate-900 transition-all duration-300 backdrop-blur-[2px] flex items-center justify-center cursor-pointer"
          >
            <Wifi className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(true)}
            title="Hide status"
            className="flex items-center gap-2 bg-slate-900/90 text-teal-400 px-3 py-1.5 rounded-full shadow-md border border-slate-800 hover:bg-slate-900 transition-all duration-300 backdrop-blur-[2px]"
          >
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse shrink-0" />
            <Wifi className="w-3.5 h-3.5 shrink-0" />
            <span className="text-xs font-semibold whitespace-nowrap">
              {pendingCount > 0 ? `${pendingCount} change(s) waiting to sync` : "POS Online"}
            </span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="hidden md:block fixed bottom-4 right-4 z-50 print:hidden select-none">
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          title="Show status"
          className="w-3 h-3 rounded-full bg-amber-500 animate-pulse block cursor-pointer p-0 border-0 outline-none"
        />
      ) : (
        <button
          onClick={() => setCollapsed(true)}
          className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full shadow-lg border border-amber-500 text-sm font-semibold animate-pulse"
        >
          <WifiOff className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap">
            Offline Mode (Saving Locally){pendingCount > 0 ? ` · ${pendingCount} pending` : ""}
          </span>
        </button>
      )}
    </div>
  );
};

export default SyncIndicator;
